import React, { useState, useEffect, useCallback, JSX } from 'react';
import { EventBus } from 'ts-bus';
import { StyleSheet, css } from 'aphrodite';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import { useNavigate } from 'react-router-dom';

import { Group, generateGroupName } from '../../server/model/property/Group';
import { defaultStyles } from '../../styling/styles';
import {
  PROPERTIES_BACKGROUND_COLOR,
  SECONDARY_TEXT_COLOR_ON_WHITE,
  SECONDARY_TEXT_COLOR,
  ACCENT_COLOR,
} from '../../styling/colors';
import { PropertyToDraw } from '../map/model/PropertyToDraw';

import {
  centerCoordinatesEvent,
  markerClickedEvent,
} from '../map/MapComponent';
import {
  AppComponentsProps,
  MAX_WIDTH_MOBILE_PHONE,
} from '../../util/AppComponentsProps';
import { NO_GROUPS_GROUP_ID } from '../../util/PropertyUtil';
import { AreaType } from '../../server/model/user/UserSettings';
import { SingleListEntryComponent } from './SingleListEntryComponent';
import { Property } from '../../server/model/property/Property';

export const CIRCLE_SIZE = 32;

let subscribedEvents: { (): void }[] = [];

export type PropertiesListComponentProps = {
  nonUsablePropertiesAmount: number;
  canAddMoreProperties: boolean;
  isInvitedProperties: boolean;
  owner: boolean;
  eventBus: EventBus;
  groupsWithProperties: GroupsWithProperties;
  showGroups: boolean;
  preferredAreaType: AreaType;
  appComponentsProps: AppComponentsProps;
  clickedPropertyDetails?: (propertyToDraw: PropertyToDraw) => void;
  clickedPaymentDetails?: (propertyToDraw: PropertyToDraw) => void;
  clickedShowSingleInvitedProperty?: (property: Property) => void;
  clickedAcceptInvitedProperty?: (property: Property) => void;
  clickedDeclineInvitedProperty?: (property: Property) => void;
};

export default function PropertiesListComponent(
  propertiesListComponentProps: PropertiesListComponentProps
) {
  const [isGroupOpened, setIsGroupOpened] = useState<{
    [groupId: number]: boolean;
  }>({});
  const { t } = useTranslation();
  const history = useNavigate();

  const changeOpenStateOfGivenGroup = useCallback(
    (groupId: number, onlyChangeToOpenState: boolean = false) => {
      if (isGroupOpened[groupId] && onlyChangeToOpenState) return;

      isGroupOpened[groupId] = !isGroupOpened[groupId];
      setIsGroupOpened(() => ({ ...isGroupOpened }));
    },
    [isGroupOpened]
  );

  const mobileScreen =
    propertiesListComponentProps.appComponentsProps.windowWidth <=
    MAX_WIDTH_MOBILE_PHONE;

  useEffect(() => {
    subscribedEvents.push(
      propertiesListComponentProps.eventBus.subscribe(
        markerClickedEvent,
        (event) => {
          const clickedPropertyId = event.payload.propertyId;
          const lastClickedPropertyId = event.payload.lastClickedPropertyId;

          if (propertiesListComponentProps.showGroups) {
            // Get property and groupPosition
            var clickedGroupPropertyToDraw: PropertyToDraw | undefined;
            let groupIdOfClickedProperty: number | undefined;
            Object.keys(
              propertiesListComponentProps.groupsWithProperties
            ).forEach((groupIdString: string) => {
              const groupId = (groupIdString as unknown) as number;
              const group =
                propertiesListComponentProps.groupsWithProperties[groupId];
              const searchedProperty = group.propertiesToDraw.find(
                (propertyToDraw) =>
                  propertyToDraw.property.id === clickedPropertyId
              );
              if (searchedProperty) {
                clickedGroupPropertyToDraw = searchedProperty;
                groupIdOfClickedProperty = groupId;
              }
            });

            // Center bounding box of property and open/scroll to group
            if (clickedGroupPropertyToDraw && groupIdOfClickedProperty) {
              // Only zoom in property in mobile view, if not already zoomed in
              if (
                !mobileScreen ||
                lastClickedPropertyId !== clickedPropertyId
              ) {
                centerBoundingBox(
                  clickedGroupPropertyToDraw.property,
                  propertiesListComponentProps.eventBus,
                  true
                );
              }

              // Do only change stuff in list in mobile view, if the user clicked the marker again when zoomed
              if (
                !mobileScreen ||
                lastClickedPropertyId === clickedPropertyId
              ) {
                changeOpenStateOfGivenGroup(groupIdOfClickedProperty, true);
                const propertyElement = document.getElementById(
                  `property-${clickedGroupPropertyToDraw.property.id}`
                );

                // Wait till the group is really opened
                setTimeout(function () {
                  if (propertyElement && !mobileScreen) {
                    propertyElement.scrollIntoView();
                  }
                }, 200);
              }
            }
          } else {
            // Get property
            const clickedPropertyToDraw = propertiesListComponentProps.groupsWithProperties[
              NO_GROUPS_GROUP_ID
            ].propertiesToDraw.find(
              (propertyToDraw) =>
                propertyToDraw.property.id === clickedPropertyId
            );

            // Center bounding box of property and scroll to item in list
            if (clickedPropertyToDraw) {
              // Only zoom in property in mobile view, if not already zoomed in
              if (
                !mobileScreen ||
                lastClickedPropertyId !== clickedPropertyId
              ) {
                centerBoundingBox(
                  clickedPropertyToDraw.property,
                  propertiesListComponentProps.eventBus,
                  true
                );
              }

              // Do only change stuff in list in mobile view, if the user clicked the marker again when zoomed
              if (
                !mobileScreen ||
                lastClickedPropertyId === clickedPropertyId
              ) {
                const propertyElement = document.getElementById(
                  `property-${clickedPropertyToDraw.property.id}`
                );
                if (propertyElement && !mobileScreen) {
                  propertyElement.scrollIntoView();
                }
              }
            }
          }
        }
      )
    );

    return () => {
      subscribedEvents.forEach((subscribedEvent) => subscribedEvent());
      subscribedEvents = [];
    };
  }, [
    changeOpenStateOfGivenGroup,
    mobileScreen,
    propertiesListComponentProps.eventBus,
    propertiesListComponentProps.groupsWithProperties,
    propertiesListComponentProps.showGroups,
  ]);

  let premiumAboInfo = undefined;
  if (propertiesListComponentProps.nonUsablePropertiesAmount > 0) {
    const firstText =
      propertiesListComponentProps.nonUsablePropertiesAmount === 1
        ? (propertiesListComponentProps.isInvitedProperties
            ? t('PAYMENT_PAGE.TOO_MUCH_INVITED_PROPERTIES_PREFIX')
            : t('PAYMENT_PAGE.TOO_MUCH_PROPERTIES_PREFIX')) +
          '1' +
          (propertiesListComponentProps.isInvitedProperties
            ? t('PAYMENT_PAGE.TOO_MUCH_INVITED_PROPERTIES_POSTFIX_SINGLE')
            : t('PAYMENT_PAGE.TOO_MUCH_PROPERTIES_POSTFIX_SINGLE'))
        : (propertiesListComponentProps.isInvitedProperties
            ? t('PAYMENT_PAGE.TOO_MUCH_INVITED_PROPERTIES_PREFIX')
            : t('PAYMENT_PAGE.TOO_MUCH_PROPERTIES_PREFIX')) +
          propertiesListComponentProps.nonUsablePropertiesAmount +
          (propertiesListComponentProps.isInvitedProperties
            ? t('PAYMENT_PAGE.TOO_MUCH_INVITED_PROPERTIES_POSTFIX_MULTIPLE')
            : t('PAYMENT_PAGE.TOO_MUCH_PROPERTIES_POSTFIX_MULTIPLE'));
    premiumAboInfo = (
      <div className={css(styles.premiumAboInfoContainer)}>
        <span className={css(styles.premiumAboInfo)}>
          {firstText}{' '}
          {
            <span
              className={css(styles.premiumAboInfoLink)}
              onClick={() => {
                history('/accountpayment');
              }}
            >
              {t('GLOBAL.HERE')}
            </span>
          }
          {t('PAYMENT_PAGE.PREMIUM_ABO_HINT')}
        </span>
      </div>
    );
  } else if (!propertiesListComponentProps.canAddMoreProperties) {
    premiumAboInfo = (
      <div className={css(styles.premiumAboInfoContainer)}>
        <span className={css(styles.premiumAboInfo)}>
          {t('PAYMENT_PAGE.ENOUGH_PROPERTIES_HINT')}{' '}
          {
            <span
              className={css(styles.premiumAboInfoLink)}
              onClick={() => {
                history('/accountpayment');
              }}
            >
              {t('GLOBAL.HERE')}
            </span>
          }
          {t('PAYMENT_PAGE.PREMIUM_ABO_HINT')}
        </span>
      </div>
    );
  }

  return (
    <div className={css(styles.container)}>
      {Object.keys(propertiesListComponentProps.groupsWithProperties).length ===
      0 ? (
        <div className={css(styles.info)}>
          {propertiesListComponentProps.isInvitedProperties
            ? t('PROPERTIES.INVITED_PROPERTIES.NO_INVITED_PROPERTIES_AVAILABLE')
            : t('PROPERTIES.LIST.NO_PROPERTIES_AVAILABLE')}
        </div>
      ) : (
        drawList(
          propertiesListComponentProps.groupsWithProperties,
          changeOpenStateOfGivenGroup,
          isGroupOpened,
          premiumAboInfo,
          propertiesListComponentProps.showGroups,
          propertiesListComponentProps.preferredAreaType,
          propertiesListComponentProps.isInvitedProperties,
          propertiesListComponentProps.eventBus,
          t,
          propertiesListComponentProps.clickedPropertyDetails,
          propertiesListComponentProps.clickedPaymentDetails,
          propertiesListComponentProps.clickedShowSingleInvitedProperty,
          propertiesListComponentProps.clickedAcceptInvitedProperty,
          propertiesListComponentProps.clickedDeclineInvitedProperty
        )
      )}
    </div>
  );
}

function drawList(
  groups: GroupsWithProperties,
  changeOpenStateOfGivenGroup: {
    (groupId: number): void;
  },
  isGroupOpened: { [groupId: number]: boolean },
  premiumAboInfo: JSX.Element | undefined,
  showGroups: boolean,
  preferredAreaType: AreaType,
  isInvitedProperties: boolean,
  eventBus: EventBus,
  t: TFunction,
  clickedPropertyDetails?: (propertyToDraw: PropertyToDraw) => void,
  clickedPaymentDetails?: (propertyToDraw: PropertyToDraw) => void,
  clickedShowSingleInvitedProperty?: (property: Property) => void,
  clickedAcceptInvitedProperty?: (property: Property) => void,
  clickedDeclineInvitedProperty?: (property: Property) => void
) {
  if (showGroups) {
    return drawGroupsList(
      groups,
      changeOpenStateOfGivenGroup,
      isGroupOpened,
      premiumAboInfo,
      preferredAreaType,
      isInvitedProperties,
      eventBus,
      t,
      clickedPropertyDetails,
      clickedPaymentDetails,
      clickedShowSingleInvitedProperty,
      clickedAcceptInvitedProperty,
      clickedDeclineInvitedProperty
    );
  }

  return drawOnlyPropertiesList(
    groups[NO_GROUPS_GROUP_ID].propertiesToDraw,
    preferredAreaType,
    isInvitedProperties,
    eventBus,
    premiumAboInfo,
    clickedPropertyDetails,
    clickedPaymentDetails,
    clickedShowSingleInvitedProperty,
    clickedAcceptInvitedProperty,
    clickedDeclineInvitedProperty
  );
}

function drawOnlyPropertiesList(
  propertiesToDraw: PropertyToDraw[],
  preferredAreaType: AreaType,
  isInvitedProperties: boolean,
  eventBus: EventBus,
  premiumAboInfo: JSX.Element | undefined,
  clickedPropertyDetails?: (propertyToDraw: PropertyToDraw) => void,
  clickedPaymentDetails?: (propertyToDraw: PropertyToDraw) => void,
  clickedShowSingleInvitedProperty?: (property: Property) => void,
  clickedAcceptInvitedProperty?: (property: Property) => void,
  clickedDeclineInvitedProperty?: (property: Property) => void
) {
  return (
    <List
      sx={{
        width: '100%',
        zIndex: 0,
        position: 'relative',
        overflow: 'auto',
        height: 'calc(100% - 16px)',
      }}>
      {propertiesToDraw.map((propertyToDraw) => (
        <SingleListEntryComponent
          isInvitedProperties={isInvitedProperties}
          key={`property-${propertyToDraw.property.id}`}
          propertyToDraw={propertyToDraw}
          preferredAreaType={preferredAreaType}
          eventBus={eventBus}
          clickedDetails={() => {
            if (clickedPropertyDetails) {
              clickedPropertyDetails(propertyToDraw);
            }
          }}
          clickedPaymentDetails={() => {
            if (clickedPaymentDetails) {
              clickedPaymentDetails(propertyToDraw);
            }
          }}
          clickedInvitedDetails={() => {
            if (clickedShowSingleInvitedProperty) {
              clickedShowSingleInvitedProperty(propertyToDraw.property);
            }
          }}
          clickedAcceptInvitation={() => {
            if (clickedAcceptInvitedProperty) {
              clickedAcceptInvitedProperty(propertyToDraw.property);
            }
          }}
          clickedDeclineInvitation={() => {
            if (clickedDeclineInvitedProperty) {
              clickedDeclineInvitedProperty(propertyToDraw.property);
            }
          }}
        />
      ))}
      {premiumAboInfo}
    </List>
  );
}

function drawGroupsList(
  groups: GroupsWithProperties,
  changeOpenStateOfGivenGroup: {
    (groupId: number): void;
  },
  isGroupOpened: { [groupId: number]: boolean },
  premiumAboInfo: JSX.Element | undefined,
  preferredAreaType: AreaType,
  isInvitedProperties: boolean,
  eventBus: EventBus,
  t: TFunction,
  clickedPropertyDetails?: (propertyToDraw: PropertyToDraw) => void,
  clickedPaymentDetails?: (propertyToDraw: PropertyToDraw) => void,
  clickedShowSingleInvitedProperty?: (property: Property) => void,
  clickedAcceptInvitedProperty?: (property: Property) => void,
  clickedDeclineInvitedProperty?: (property: Property) => void
) {
  const groupIds: number[] = [];
  for (const groupId in groups) {
    groupIds.push((groupId as unknown) as number);
  }

  return (
    <List 
      sx={{
        width: '100%',
        zIndex: 0,
        position: 'relative',
        overflow: 'auto',
        height: 'calc(100% - 16px)',
      }}>
      {groupIds.map((groupId) => (
        <li
          key={`group-${groupId}`}
          className={css(styles.listSection)}>
          <ul className={css(styles.ul)}>
            <ListItem
              onClick={() => changeOpenStateOfGivenGroup(groupId)}
            >
              <ListItemIcon>
                <div
                  className={css(
                    StyleSheet.create({
                      style: {
                        ...runtimeStyles.groupCircle,
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: groups[groupId].color,
                      },
                    }).style
                  )}
                />
              </ListItemIcon>
              <ListItemText>
                <div className={css(styles.groupTextContainer)}>
                  <div className={css(styles.groupTextHeader)}>
                    {generateGroupName(groups[groupId].groupData)}
                  </div>
                  <div className={css(styles.groupTextFooter)}>
                    {getAmountOfPropertiesForGivenGroup(
                      groups[groupId].propertiesToDraw,
                      t
                    )}
                  </div>
                </div>
              </ListItemText>
              {isGroupOpened[groupId] ? (
                <img
                  src={window.location.origin + '/images/ExpandLess.svg'}
                  alt="ExpandLess"
                />
              ) : (
                <img
                  src={window.location.origin + '/images/ExpandMore.svg'}
                  alt="ExpandMore"
                />
              )}
            </ListItem>
            <Collapse in={isGroupOpened[groupId]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {groups[groupId].propertiesToDraw.map((propertyToDraw) => (
                  <SingleListEntryComponent
                    isInvitedProperties={isInvitedProperties}
                    key={`property-${propertyToDraw.property.id}`}
                    propertyToDraw={propertyToDraw}
                    preferredAreaType={preferredAreaType}
                    eventBus={eventBus}
                    clickedDetails={() => {
                      if (clickedPropertyDetails) {
                        clickedPropertyDetails(propertyToDraw);
                      }
                    }}
                    clickedPaymentDetails={() => {
                      if (clickedPaymentDetails) {
                        clickedPaymentDetails(propertyToDraw);
                      }
                    }}
                    clickedInvitedDetails={() => {
                      if (clickedShowSingleInvitedProperty) {
                        clickedShowSingleInvitedProperty(
                          propertyToDraw.property
                        );
                      }
                    }}
                    clickedAcceptInvitation={() => {
                      if (clickedAcceptInvitedProperty) {
                        clickedAcceptInvitedProperty(propertyToDraw.property);
                      }
                    }}
                    clickedDeclineInvitation={() => {
                      if (clickedDeclineInvitedProperty) {
                        clickedDeclineInvitedProperty(propertyToDraw.property);
                      }
                    }}
                  />
                ))}
              </List>
            </Collapse>
          </ul>
        </li>
      ))}
      {premiumAboInfo}
    </List>
  );
}

export function centerBoundingBox(
  property: Property,
  eventBus: EventBus,
  switchToMapView: boolean
) {
  eventBus.publish(
    centerCoordinatesEvent({
      switchToMapViewOnMobileDevice: switchToMapView,
      coordinates: property.coordinates
    })
  );
}

function getAmountOfPropertiesForGivenGroup(
  propertiesToDraw: PropertyToDraw[],
  t: TFunction
): string {
  const amount = propertiesToDraw.length;

  if (amount === 1) return amount + t('PROPERTIES.LIST.ONE_PROPERTY');
  return amount + t('PROPERTIES.LIST.MULTIPLE_PROPERTIES');
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    position: 'relative',
    overflow: 'auto',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  groupTextContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  groupTextHeader: {
    ...defaultStyles.textStyle,
  },
  groupTextFooter: {
    ...defaultStyles.textStyle,
    color: SECONDARY_TEXT_COLOR_ON_WHITE,
  },
  info: {
    ...defaultStyles.textStyle,
    textAlign: 'center',
  },
  premiumAboInfoContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: 20,
    alignItems: 'center',
  },
  premiumAboInfo: {
    ...defaultStyles.textStyle,
    padding: 20,
    color: SECONDARY_TEXT_COLOR,
    textAlign: 'center',
  },
  premiumAboInfoLink: {
    ...defaultStyles.textStyle,
    color: ACCENT_COLOR,
    textAlign: 'center',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  listSection: {
    backgroundColor: PROPERTIES_BACKGROUND_COLOR,
    marginLeft: 7,
    marginRight: 7,
    marginTop: 10,
    marginBottom: 10,
  },
  ul: {
    backgroundColor: PROPERTIES_BACKGROUND_COLOR,
    padding: 0,
  },
});

const runtimeStyles = {
  groupCircle: {
    borderRadius: CIRCLE_SIZE * 2,
    width: CIRCLE_SIZE,
    height: CIRCLE_SIZE,
    justifyContent: 'center',
    alignItems: 'center',
  },
};

export type GroupsWithProperties = {
  [groupId: number]: {
    propertiesToDraw: PropertyToDraw[];
    color: string;
    groupData?: Group;
  };
};
